/**
 * A default token provider that reads the bearer token from Web Storage.
 *
 * EMIL apps persist the active account's session under a localStorage key
 * (`APP_TOKEN` by default) as `{"username": "...", "accessToken": "<jwt>"}`. The
 * React providers use this as their default `token` source, so an app can mount
 * `<CapabilitiesProvider clientSdkKey={…} />` with **no token wiring** and still
 * send an authenticated, tenant-scoped request — while any app that keeps its
 * token elsewhere overrides it by passing its own `token`.
 *
 * It is deliberately forgiving: a missing key, blocked/absent storage (SSR,
 * sandboxed iframe, private mode), malformed JSON, or a missing field all
 * resolve to `undefined` (no `Authorization` header) rather than throwing.
 */
/** The function form of a token provider — what this factory returns. */
type TokenGetter = () => string | undefined;

export interface LocalStorageTokenOptions {
  /** localStorage key holding the session payload. Default `"APP_TOKEN"`. */
  key?: string;
  /**
   * Field within the parsed JSON object that holds the bearer token. Default
   * `"accessToken"`. Ignored when the stored value is a bare token string.
   */
  field?: string;
  /**
   * Storage backend. Defaults to `localStorage`. Pass `sessionStorage` or a
   * custom `Storage` (e.g. in tests) to read from somewhere else.
   */
  storage?: Storage;
}

/** Resolve a usable Storage, tolerating environments where access throws. */
function resolveStorage(explicit?: Storage): Storage | undefined {
  if (explicit) return explicit;
  try {
    if (typeof localStorage !== "undefined") return localStorage;
  } catch {
    // Accessing localStorage can throw in sandboxed iframes / blocked storage.
  }
  return undefined;
}

/**
 * Build a token provider that reads the token from Web Storage on each
 * call (so it always reflects the currently-active account, e.g. after a tenant
 * switch). See {@link LocalStorageTokenOptions} for the key / field / backend.
 *
 * ```ts
 * // defaults: reads APP_TOKEN.accessToken from localStorage
 * token: localStorageToken()
 * // custom key/field:
 * token: localStorageToken({ key: "auth", field: "jwt" })
 * ```
 */
export function localStorageToken(options: LocalStorageTokenOptions = {}): TokenGetter {
  const key = options.key ?? "APP_TOKEN";
  const field = options.field ?? "accessToken";

  return () => {
    const storage = resolveStorage(options.storage);
    if (!storage) return undefined;

    let raw: string | null;
    try {
      raw = storage.getItem(key);
    } catch {
      return undefined;
    }
    if (!raw) return undefined;

    // The value is normally `{"accessToken": "<jwt>"}`, but tolerate a bare
    // token stored either as raw text or as a JSON string.
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Not JSON (a bare JWT has no quotes) — treat the value as the token.
      return raw;
    }

    if (typeof parsed === "string") {
      return parsed || undefined;
    }
    if (parsed && typeof parsed === "object") {
      const value = (parsed as Record<string, unknown>)[field];
      return typeof value === "string" && value ? value : undefined;
    }
    return undefined;
  };
}
